import { useRef, useState, type DragEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiWrite, list, uploadDocument, type Client, type DocumentRow } from '../lib/api'
import { useDocuments, useExtraction } from '../lib/queries'
import { Empty, PageHead, Skel } from '../components/ui'
import { thaiDate } from '../lib/format'

const kinds: Record<string, string> = {
  invoice: 'ใบแจ้งหนี้', receipt: 'ใบเสร็จ', tax_invoice: 'ใบกำกับภาษี',
  bank_statement: 'รายการเดินบัญชี', contract: 'สัญญา', payroll: 'เงินเดือน',
  ledger_export: 'ส่งออกบัญชี', purchase_order: 'ใบสั่งซื้อ', delivery_note: 'ใบส่งของ', other: 'อื่น ๆ',
}
const fmtSize = (b: number) => (b < 1024 ? `${b} B` : `${(b / 1024).toFixed(0)} KB`)

const statusLabel: Record<string, string> = {
  uploaded: 'รออัปโหลด', processing: 'กำลังประมวลผล', ready: 'พร้อมใช้',
  failed: 'ล้มเหลว', quarantined: 'กักกัน', archived: 'เก็บถาวร',
}

export function Documents() {
  const q = useDocuments()
  const clients = useQuery({ queryKey: ['clients'], queryFn: () => list<Client>('/v1/clients'), staleTime: 60_000 })
  const qc = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [kind, setKind] = useState('tax_invoice')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)

  const rows = q.data ?? []
  const clientId = clients.data?.[0]?.id ?? ''

  async function send(files: FileList | File[]) {
    if (!clientId) { setError('ยังไม่มีนิติบุคคลในระบบ'); return }
    setError(null)
    for (const file of Array.from(files)) {
      if (file.size > 104857600) { setError(`${file.name} ใหญ่เกิน 100 MB`); continue }
      setBusy(file.name)
      try {
        await uploadDocument(file, clientId, '', kind)
        setDone(file.name)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'อัปโหลดไม่สำเร็จ')
      } finally {
        setBusy(null)
      }
    }
    await qc.invalidateQueries({ queryKey: ['documents'] })
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragging(false)
    if (e.dataTransfer.files.length) void send(e.dataTransfer.files)
  }

  return (
    <>
      <PageHead title="เอกสาร" sub={`${rows.length} ไฟล์ · เก็บแบบ immutable พร้อมค่าแฮช SHA-256`} />

      <div
        className="card"
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        style={{
          padding: 28, marginBottom: 18, textAlign: 'center',
          borderStyle: 'dashed',
          borderColor: dragging ? 'var(--color-gold)' : 'var(--color-hairline-strong)',
          background: dragging ? 'rgba(200,169,106,0.05)' : undefined,
          transition: 'border-color 120ms, background 120ms',
        }}
      >
        <input ref={inputRef} type="file" multiple hidden
          onChange={(e) => { if (e.target.files) void send(e.target.files); e.target.value = '' }} />
        <div className="eyebrow">อัปโหลดอย่างปลอดภัย</div>
        <div className="heading-m" style={{ marginTop: 10 }}>ลากไฟล์มาวาง หรือเลือกจากเครื่อง</div>
        <p className="dim" style={{ fontSize: 12, marginTop: 8 }}>
          รองรับ PDF, รูปภาพ, CSV, XLSX · ไม่เกิน 100 MB · ตรวจค่าแฮชก่อนบันทึก
        </p>
        <div className="row" style={{ justifyContent: 'center', marginTop: 18, gap: 10 }}>
          <select className="select" value={kind} onChange={(e) => setKind(e.target.value)} aria-label="ประเภทเอกสาร">
            {Object.entries(kinds).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button className="btn btn-primary" disabled={Boolean(busy)} onClick={() => inputRef.current?.click()}>
            {busy ? `กำลังอัปโหลด ${busy}` : 'เลือกไฟล์'}
          </button>
        </div>
        {error ? <div className="form-error" style={{ maxWidth: 460, margin: '14px auto 0' }}>{error}</div> : null}
        {done && !error ? <div className="dim" style={{ marginTop: 12, fontSize: 12 }}>บันทึกแล้ว: {done}</div> : null}
      </div>

      {q.isLoading ? <div className="stack"><Skel h={52} /><Skel h={52} /><Skel h={52} /></div>
        : rows.length === 0 ? <div className="card"><Empty title="ยังไม่มีเอกสาร" body="อัปโหลดใบกำกับภาษีหรือรายการเดินบัญชีเพื่อเริ่มต้น" /></div>
        : (
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>ชื่อไฟล์</th><th>ประเภท</th><th>หน้า</th><th>ขนาด</th><th>สถานะ</th><th>อัปโหลด</th></tr></thead>
              <tbody>
                {rows.map((d: DocumentRow) => (
                  <tr key={d.id} onClick={() => setOpenId(d.id)}>
                    <td className="mono">{d.original_name}</td>
                    <td>{kinds[d.kind] ?? d.kind}</td>
                    <td className="tnum">{d.page_count || '—'}</td>
                    <td className="tnum dim">{fmtSize(d.size_bytes)}</td>
                    <td><span className={`chip${d.status === 'ready' ? ' gold' : ''}`}>{statusLabel[d.status] ?? d.status}</span></td>
                    <td className="dim">{thaiDate(d.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {openId ? <ExtractionDrawer id={openId} onClose={() => setOpenId(null)} /> : null}
    </>
  )
}

function ExtractionDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useExtraction(id)
  const d = q.data
  const qc = useQueryClient()
  const [reason, setReason] = useState('')
  const [askErase, setAskErase] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const erase = useMutation({
    mutationFn: () => apiWrite(`/v1/documents/${id}/erase`, 'POST', { reason }),
    onSuccess: () => {
      setNotice('ลบข้อมูลเรียบร้อย — หลักฐานการลบถูกบันทึกใน audit trail')
      setAskErase(false)
      void qc.invalidateQueries({ queryKey: ['documents'] })
      setTimeout(onClose, 1400)
    },
    onError: (e) => setNotice(e instanceof Error ? e.message : 'ลบไม่สำเร็จ'),
  })
  const hold = useMutation({
    mutationFn: (h: boolean) => apiWrite(`/v1/documents/${id}/legal-hold`, 'POST', { hold: h, reason: 'จากหน้าจอเอกสาร' }),
    onSuccess: (_d, h) => setNotice(h ? 'อายัดเอกสารแล้ว' : 'ปลดอายัดแล้ว'),
    onError: (e) => setNotice(e instanceof Error ? e.message : 'ทำรายการไม่สำเร็จ'),
  })
  const label: Record<string, string> = {
    pending: 'รอสกัดข้อมูล', running: 'กำลังสกัด…', succeeded: 'สกัดสำเร็จ',
    failed: 'สกัดไม่สำเร็จ', unsupported: 'ไม่มีไฟล์ต้นฉบับ / ต้องใช้ OCR',
  }
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'var(--color-scrim)', backdropFilter: 'blur(3px)' }}>
      <aside onClick={(e) => e.stopPropagation()} className="card rise" style={{
        position: 'absolute', top: 0, right: 0, height: '100%', width: 'min(480px, 94vw)',
        borderRadius: 0, borderLeft: '1px solid var(--color-hairline)', padding: 26, overflowY: 'auto',
        boxShadow: 'var(--shadow-sheet)', background: 'var(--color-surface-1)',
      }}>
        <div className="row" style={{ alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="eyebrow">ข้อมูลที่สกัดได้</div>
            <div className="heading-m" style={{ marginTop: 8 }}>{label[d?.status ?? 'pending'] ?? d?.status}</div>
          </div>
          <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }} onClick={onClose}>ปิด</button>
        </div>

        {d?.result ? (
          <div className="row" style={{ marginTop: 18, gap: 10, flexWrap: 'wrap' }}>
            <span className={`chip${d.result.valid ? ' gold' : ''}`}>{d.result.valid ? 'ยอดสอดคล้อง' : 'ยอดไม่สอดคล้อง'}</span>
            <span className="chip">ความมั่นใจ {(d.result.confidence * 100).toFixed(0)}%</span>
            <span className="chip">{d.result.kind}</span>
            {d.engine ? <span className="chip">{d.engine}</span> : null}
          </div>
        ) : null}

        {d?.error ? <div className="form-error" style={{ marginTop: 16 }}>{d.error}</div> : null}

        <div style={{ marginTop: 22 }}>
          {(d?.fields ?? []).map((f) => (
            <div key={f.field} className="evidence-row" style={{ gridTemplateColumns: '130px minmax(0,1fr) 56px' }}>
              <span className="dim mono" style={{ fontSize: 11 }}>{f.field}</span>
              <span className="tnum">{f.value_text ?? f.value_num ?? '—'}</span>
              <span className="dim tnum" style={{ fontSize: 11, textAlign: 'right' }}>{(f.confidence * 100).toFixed(0)}%</span>
            </div>
          ))}
          {d && d.fields.length === 0 && d.status !== 'pending' && d.status !== 'running'
            ? <p className="dim" style={{ fontSize: 12 }}>ไม่มีฟิลด์ที่สกัดได้</p> : null}
        </div>

        <div className="rule" style={{ margin: '22px 0 16px' }} />
        <div className="eyebrow">การปฏิบัติตาม PDPA</div>
        <div className="row" style={{ marginTop: 10, gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost btn-sm" disabled={hold.isPending} onClick={() => hold.mutate(true)}>อายัดทางกฎหมาย</button>
          <button className="btn btn-ghost btn-sm" disabled={hold.isPending} onClick={() => hold.mutate(false)}>ปลดอายัด</button>
          <button className="btn btn-sm" style={{ marginLeft: 'auto', color: 'var(--color-danger, #c97a7a)' }}
            onClick={() => setAskErase(true)}>ลบข้อมูลถาวร</button>
        </div>
        {askErase ? (
          <div style={{ marginTop: 12 }}>
            <div className="dim" style={{ fontSize: 11.5, marginBottom: 6 }}>
              การลบถาวรจะลบไฟล์ ข้อมูลที่สกัดได้ และไม่สามารถย้อนกลับได้ (ถ้าอายัดอยู่จะถูกล็อก)
            </div>
            <input className="input" placeholder="เหตุผลในการลบ (อย่างน้อย 5 ตัวอักษร)"
              value={reason} onChange={(e) => setReason(e.target.value)} />
            <div className="row" style={{ marginTop: 8, gap: 8 }}>
              <button className="btn btn-sm" disabled={reason.trim().length < 5 || erase.isPending}
                onClick={() => erase.mutate()}>{erase.isPending ? 'กำลังลบ…' : 'ยืนยันลบ'}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setAskErase(false)}>ยกเลิก</button>
            </div>
          </div>
        ) : null}
        {notice ? <div className="dim" style={{ marginTop: 10, fontSize: 12 }}>{notice}</div> : null}

        {d?.result?.warnings?.length ? (
          <div style={{ marginTop: 20 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>คำเตือน</div>
            {d.result.warnings.map((w) => (
              <div key={w} style={{ fontSize: 12, color: 'var(--color-amber)', marginBottom: 6 }}>• {w}</div>
            ))}
          </div>
        ) : null}
      </aside>
    </div>
  )
}
